import React from 'react';
import { getIntl } from '../../../utils/intl';
import { APITypes, MedicalDataTypes } from '../../../primary-record-types';
import * as Card from '../../v2BaseComponents/Card';
import addressToString from '../../../utils/addressToString';
import LazyLoadedReferenceCounts from './LazyLoadedReferenceCounts';
import { getVisitTypeDisplay } from '../../../utils/sharedMessages';
import LargeDateDisplay from '../LargeDateDisplay';
import Stack from '../../v2BaseComponents/Stack';
import { Checkbox } from '../../v2BaseComponents/Inputs';
import { useResourceTitle } from '../../../utils/resourceUtils/getResourceTitle';
import { View } from 'react-native';

const intl = getIntl('visit-resource-card', {
    visit: 'Visit',
    upcoming: 'Upcoming',
});

interface VisitCardProps {
    visit: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordVisit>;
    profileId: string;
    onPress?: () => void;
    isSelectable?: boolean;
    isSelected?: boolean;
    isDisabled?: boolean;
    flat?: boolean;
}

const VisitCard: React.FC<VisitCardProps> = ({
    visit,
    profileId,
    onPress,
    isSelectable,
    isSelected,
    isDisabled,
    flat,
}) => {
    const visitName = useResourceTitle(visit, profileId);
    const isUpcoming = new Date(visit.startDateTime || 0).getTime() > new Date().getTime();

    const maybeRenderCheckbox = (content: React.ReactNode) =>
        isSelectable ? (
            <Card.CardLeftContent
                leftContent={
                    <Stack vertical align="center" justify="center" fullHeight>
                        <Checkbox value={!!isSelected} onChange={() => onPress?.()} title="" isDisabled={isDisabled} />
                    </Stack>
                }
            >
                {content}
            </Card.CardLeftContent>
        ) : (
            content
        );

    const content = (
        <Card.CardLeftContent
            leftContent={
                <View style={{ alignSelf: 'center' }}>
                    <LargeDateDisplay date={visit.startDateTime} />
                </View>
            }
        >
            <Card.CardContent>
                <Card.CardInlineContent>
                    <View style={{ flex: 1, minWidth: 0 }}>
                        <Card.CardHeader>{visitName}</Card.CardHeader>
                    </View>
                    {isUpcoming ? (
                        <View style={{ flexShrink: 0 }}>
                            <Card.CardChip title={intl('upcoming')} />
                        </View>
                    ) : null}
                </Card.CardInlineContent>
                {visit.providerName && <Card.CardBody>{visit.providerName}</Card.CardBody>}
                <Card.CardBody2>{addressToString(visit)}</Card.CardBody2>
                {visit.visitType && <Card.CardBody2>{getVisitTypeDisplay(visit)}</Card.CardBody2>}
                <LazyLoadedReferenceCounts resource={visit} medicalProfileId={profileId} />
            </Card.CardContent>
        </Card.CardLeftContent>
    );

    return (
        <Card.Card key={visit.id} onPress={onPress} flat={flat}>
            {maybeRenderCheckbox(content)}
        </Card.Card>
    );
};

export default React.memo(VisitCard);
